import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowLeftIcon, DownloadIcon, ShieldCheckIcon } from "lucide-react"
import { useState } from "react"
import { z } from "zod"

import { ExportDownloading, SignIn } from "@/auth/components/sign-in"
import { OAUTH_ENABLED, OAUTH_ERRORS } from "@/auth/oauth"
import { isExportRedirect } from "@/auth/redirect"
import {
  getLoginPageData,
  loginFormAction,
  loginFormSchema,
  requireAuthConfiguration,
  requireGuestUser,
} from "@/auth/rpc"
import { Turnstile } from "@/cloudflare/turnstile"
import { Logo } from "@/core/components/logo"
import { getSiteName } from "@/core/site"
import { useFormAction } from "@/form"

const loginSearchSchema = z.object({
  redirectTo: z.string().optional().catch("/"),
  // Set by the Google / Apple callback (T1414); only the fixed codes of `OAUTH_ERRORS` survive
  oauthError: z.enum(OAUTH_ERRORS).optional().catch(undefined),
})

export const Route = createFileRoute("/login")({
  validateSearch: (search) => loginSearchSchema.parse(search),
  beforeLoad: async ({ search }) => {
    await requireAuthConfiguration()
    await requireGuestUser({ data: { redirectTo: search.redirectTo } })
  },
  loaderDeps: ({ search }) => ({ redirectTo: search.redirectTo ?? "/" }),
  loader: async ({ deps }) => {
    return getLoginPageData({ data: { redirectTo: deps.redirectTo } })
  },
  component: Login,
})

function Login() {
  const { cloudflareTurnstilePublicKey, redirectTo } = Route.useLoaderData()
  const { oauthError } = Route.useSearch()
  const queryClient = useQueryClient()
  const { data, error, isPending, mutate, onSubmit } = useFormAction(
    loginFormSchema,
    loginFormAction,
    {
      onSuccess: (result) => {
        setUseOtherEmail(false)
        // A verified code throws a redirect, which the router follows and which resolves with no result. For the
        // export the redirect is a document navigation to an attachment, so this page stays open (T780).
        if (!result) setSignedIn(true)
        // Fires after the client-side redirect, on the same QueryClient: a tab whose session expired may now hold
        // a different user, so never reuse data cached for the previous one (it also runs on the OTP-send step, where it is harmless).
        void queryClient.resetQueries()
      },
    },
  )
  const [useOtherEmail, setUseOtherEmail] = useState(false)
  const [signedIn, setSignedIn] = useState(false)
  const siteName = getSiteName()
  const { email, success, error: actionError } = data ?? {}
  const turnstileSubjectKey = isPending ? "pending" : "idle"

  return (
    <div className="bg-background flex min-h-screen flex-col">
      <header className="bg-card flex h-14 items-center gap-3 border-b px-4 sm:px-8 lg:px-12">
        <span className="flex items-center gap-2.5 font-bold">
          <Logo className="size-7.5 shrink-0" />
          {siteName}
        </span>
        <span className="flex-1" />
        <Link
          to="/"
          className="text-muted-foreground hover:text-foreground inline-flex min-h-11 items-center gap-1.5 text-sm font-medium min-[769px]:min-h-6"
        >
          <ArrowLeftIcon aria-hidden="true" className="size-4" />
          Back to website
        </Link>
      </header>
      <div className="grid flex-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <aside
          aria-label={`About ${siteName}`}
          className="bg-primary/5 hidden flex-col gap-7 border-r px-12 py-14 lg:flex"
        >
          <div>
            <p className="text-primary text-xs font-semibold tracking-widest uppercase">
              Built for Australian sole traders
            </p>
            <p className="mt-2 max-w-[16ch] text-3xl leading-tight font-bold tracking-tight">
              Books, invoices and mileage in one place
            </p>
          </div>
          <ul className="text-muted-foreground mt-auto flex flex-wrap gap-x-5 gap-y-2 border-t pt-5 text-xs font-medium">
            <li className="inline-flex items-center gap-2">
              <ShieldCheckIcon aria-hidden="true" className="text-primary size-4" />
              Records stored in Australia
            </li>
            <li className="inline-flex items-center gap-2">
              <DownloadIcon aria-hidden="true" className="text-primary size-4" />
              Export your records whenever you need them
            </li>
          </ul>
        </aside>
        <main className="grid place-items-center px-4 py-8 sm:px-8">
          {signedIn && isExportRedirect(redirectTo) ? (
            <ExportDownloading />
          ) : (
            <form onSubmit={onSubmit} noValidate className="w-full max-w-md">
              <input type="hidden" name="redirectTo" value={redirectTo} />
              <SignIn
                oauth={OAUTH_ENABLED ? { redirectTo } : undefined}
                oauthError={useOtherEmail || data ? undefined : oauthError}
                isPending={isPending}
                email={useOtherEmail ? undefined : email}
                onResend={(form) => {
                  const fd = new FormData(form)
                  fd.delete("otp")
                  mutate(fd)
                }}
                // a failed send leaves `data` from the previous step: its message is stale, the mutation error is not
                error={error?.message ?? (useOtherEmail ? undefined : actionError)}
                success={useOtherEmail ? undefined : success}
                onUseDifferentEmail={() => setUseOtherEmail(true)}
              />
              {cloudflareTurnstilePublicKey && (
                <div className="mt-4 flex justify-center">
                  <Turnstile
                    siteKey={cloudflareTurnstilePublicKey}
                    subjectKey={turnstileSubjectKey}
                  />
                </div>
              )}
            </form>
          )}
        </main>
      </div>
    </div>
  )
}
